import {
  isValidHexColor,
  normalizeHexColor,
  sanitizeCompanyLogoUrl,
  DEFAULT_BRAND_PRIMARY,
  DEFAULT_BRAND_SECONDARY,
} from '@/utils/branding';
import { supabase } from '../../supabase';
import { safeSupabaseOperation } from '../error-handler';

/** Claves de pantallas del panel organizador (coinciden con la columna organizer_panel_screens). */
export const ORGANIZER_PANEL_SCREEN_KEYS = [
  'dashboard',
  'events',
  'attendees',
  'networking',
  'analytics',
  'notifications',
  'settings',
] as const;

export type OrganizerPanelScreenKey = (typeof ORGANIZER_PANEL_SCREEN_KEYS)[number];

export interface OrganizationBrandingFields {
  logoUrl?: string | null;
  colorPrimary: string;
  colorSecondary: string;
}

export interface Organization {
  id: string;
  name: string;
  ownerId: string;
  createdAt: string;
  /** Pantallas habilitadas en el panel organizador. Null/undefined = todas. */
  organizerPanelScreens?: OrganizerPanelScreenKey[] | null;
  logoUrl?: string | null;
  colorPrimary?: string;
  colorSecondary?: string;
}

export interface OrgMember {
  id: string;
  orgId: string;
  userId: string;
  role: 'org_admin' | 'org_member';
  createdAt: string;
}

function mapOrgFromDB(row: any): Organization {
  const raw = row.organizer_panel_screens;
  const organizerPanelScreens =
    Array.isArray(raw) && raw.length > 0
      ? (raw as string[]).filter((k: string) =>
          (ORGANIZER_PANEL_SCREEN_KEYS as readonly string[]).includes(k)
        )
      : undefined;
  return {
    id: row.id,
    name: row.name,
    ownerId: row.owner_id,
    createdAt: row.created_at,
    organizerPanelScreens: organizerPanelScreens?.length ? (organizerPanelScreens as OrganizerPanelScreenKey[]) : undefined,
    logoUrl: row.logo_url ?? null,
    colorPrimary: row.color_primary ?? DEFAULT_BRAND_PRIMARY,
    colorSecondary: row.color_secondary ?? DEFAULT_BRAND_SECONDARY,
  };
}

const ORG_SELECT_FIELDS =
  'id, name, owner_id, created_at, organizer_panel_screens, logo_url, color_primary, color_secondary';

function mapOrgMemberFromDB(row: any): OrgMember {
  return {
    id: row.id,
    orgId: row.org_id,
    userId: row.user_id,
    role: row.role,
    createdAt: row.created_at,
  };
}

/**
 * Returns true if the user is org_admin in at least one organization (or would be after migration).
 * Used for hasOrganizerAccess (organizer portal entry).
 */
export async function hasOrgAdminRole(userId: string): Promise<boolean> {
  const result = await safeSupabaseOperation(
    () =>
      supabase
        .from('org_members')
        .select('id')
        .eq('user_id', userId)
        .eq('role', 'org_admin')
        .limit(1)
        .maybeSingle(),
    'hasOrgAdminRole'
  );
  return !!result.data;
}

/**
 * Returns org_ids where the user has org_admin role.
 * Used to scope organizer queries to the user's own organizations only.
 */
export async function getOrganizerOrgIds(userId: string): Promise<string[]> {
  const result = await safeSupabaseOperation(
    () =>
      supabase
        .from('org_members')
        .select('org_id')
        .eq('user_id', userId)
        .eq('role', 'org_admin'),
    'getOrganizerOrgIds'
  );
  if (result.error || !result.data || !Array.isArray(result.data)) return [];
  return result.data.map((r: Record<string, string>) => r.org_id);
}

export const organizationsService = {
  /**
   * Organizations where the current user is owner or member (RLS filters).
   */
  getMyOrganizations: async (): Promise<Organization[]> => {
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('organizations')
          .select(ORG_SELECT_FIELDS)
          .order('created_at', { ascending: false }),
      'getMyOrganizations'
    );
    if (result.error || !result.data) return [];
    return result.data.map(mapOrgFromDB);
  },

  getOrganization: async (orgId: string): Promise<Organization | null> => {
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('organizations')
          .select(ORG_SELECT_FIELDS)
          .eq('id', orgId)
          .single(),
      'getOrganization'
    );
    if (result.error || !result.data) return null;
    return mapOrgFromDB(result.data);
  },

  createOrganization: async (
    name: string,
    ownerId: string
  ): Promise<{ organization: Organization | null; error: string | null }> => {
    const trimmed = name.trim();
    if (!trimmed) {
      return { organization: null, error: 'El nombre de la organización es obligatorio' };
    }

    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('organizations')
          .insert({ name: trimmed, owner_id: ownerId })
          .select(ORG_SELECT_FIELDS)
          .single(),
      'createOrganization'
    );
    if (result.error || !result.data) {
      return {
        organization: null,
        error: result.error ?? 'No se pudo crear la organización',
      };
    }
    return { organization: mapOrgFromDB(result.data), error: null };
  },

  updateBranding: async (
    orgId: string,
    branding: Partial<OrganizationBrandingFields>
  ): Promise<Organization | null> => {
    const payload: Record<string, unknown> = {};
    if (branding.logoUrl !== undefined) {
      payload.logo_url =
        branding.logoUrl === null || branding.logoUrl === ''
          ? null
          : sanitizeCompanyLogoUrl(branding.logoUrl);
      if (branding.logoUrl && !payload.logo_url) {
        return null;
      }
    }
    if (branding.colorPrimary !== undefined) {
      const primary = normalizeHexColor(branding.colorPrimary, DEFAULT_BRAND_PRIMARY);
      if (!isValidHexColor(primary)) return null;
      payload.color_primary = primary;
    }
    if (branding.colorSecondary !== undefined) {
      const secondary = normalizeHexColor(branding.colorSecondary, DEFAULT_BRAND_SECONDARY);
      if (!isValidHexColor(secondary)) return null;
      payload.color_secondary = secondary;
    }
    if (Object.keys(payload).length === 0) return null;

    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('organizations')
          .update(payload)
          .eq('id', orgId)
          .select(ORG_SELECT_FIELDS)
          .single(),
      'updateBranding'
    );
    if (result.error || !result.data) return null;
    return mapOrgFromDB(result.data);
  },

  updateOrganization: async (
    orgId: string,
    updates: { name?: string; organizerPanelScreens?: OrganizerPanelScreenKey[] | null }
  ): Promise<Organization | null> => {
    const payload: Record<string, unknown> = {};
    if (updates.name !== undefined) payload.name = updates.name.trim();
    if (updates.organizerPanelScreens !== undefined) {
      payload.organizer_panel_screens =
        updates.organizerPanelScreens == null || updates.organizerPanelScreens.length === 0
          ? null
          : updates.organizerPanelScreens;
    }
    if (Object.keys(payload).length === 0) return null;
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('organizations')
          .update(payload)
          .eq('id', orgId)
          .select(ORG_SELECT_FIELDS)
          .single(),
      'updateOrganization'
    );
    if (result.error || !result.data) return null;
    return mapOrgFromDB(result.data);
  },

  /** Actualiza solo las pantallas habilitadas del panel organizador para una organización. */
  updateOrganizerPanelScreens: async (
    orgId: string,
    screens: OrganizerPanelScreenKey[]
  ): Promise<boolean> => {
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('organizations')
          .update({
            organizer_panel_screens: screens.length === 0 ? null : screens,
          })
          .eq('id', orgId),
      'updateOrganizerPanelScreens'
    );
    return !result.error;
  },

  getMembers: async (orgId: string): Promise<OrgMember[]> => {
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('org_members')
          .select('id, org_id, user_id, role, created_at')
          .eq('org_id', orgId)
          .order('created_at', { ascending: false }),
      'getMembers'
    );
    if (result.error || !result.data) return [];
    return result.data.map(mapOrgMemberFromDB);
  },

  addMember: async (
    orgId: string,
    userId: string,
    role: 'org_admin' | 'org_member'
  ): Promise<{ member: OrgMember | null; error: string | null }> => {
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('org_members')
          .insert({ org_id: orgId, user_id: userId, role })
          .select('id, org_id, user_id, role, created_at')
          .single(),
      'addMember'
    );
    if (result.error || !result.data) {
      return { member: null, error: result.error ?? 'No se pudo añadir el miembro' };
    }
    return { member: mapOrgMemberFromDB(result.data), error: null };
  },

  removeMember: async (orgId: string, userId: string): Promise<boolean> => {
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('org_members')
          .delete()
          .eq('org_id', orgId)
          .eq('user_id', userId),
      'removeMember'
    );
    return !result.error;
  },

  /**
   * Asigna un usuario a una organización (admin/superadmin). Quita otras membresías (v1: una org por usuario).
   */
  assignUserToOrganization: async (
    orgId: string,
    userId: string,
    role: 'org_admin' | 'org_member' = 'org_admin'
  ): Promise<{ success: boolean; error: string | null }> => {
    const result = await safeSupabaseOperation(
      async () => {
        const { error } = await supabase.rpc('admin_assign_org_member', {
          p_org_id: orgId,
          p_user_id: userId,
          p_role: role,
        });
        return { data: null, error };
      },
      'assignUserToOrganization'
    );
    if (result.error) {
      return { success: false, error: result.error };
    }
    return { success: true, error: null };
  },

  /** Quita un miembro de una organización (admin/superadmin). */
  adminRemoveMember: async (
    orgId: string,
    userId: string
  ): Promise<{ success: boolean; error: string | null }> => {
    const result = await safeSupabaseOperation(
      async () => {
        const { error } = await supabase.rpc('admin_remove_org_member', {
          p_org_id: orgId,
          p_user_id: userId,
        });
        return { data: null, error };
      },
      'adminRemoveOrgMember'
    );
    if (result.error) {
      return { success: false, error: result.error };
    }
    return { success: true, error: null };
  },

  updateMemberRole: async (
    orgId: string,
    userId: string,
    role: 'org_admin' | 'org_member'
  ): Promise<OrgMember | null> => {
    const result = await safeSupabaseOperation(
      () =>
        supabase
          .from('org_members')
          .update({ role })
          .eq('org_id', orgId)
          .eq('user_id', userId)
          .select('id, org_id, user_id, role, created_at')
          .single(),
      'updateMemberRole'
    );
    if (result.error || !result.data) return null;
    return mapOrgMemberFromDB(result.data);
  },

  /** Elimina una organización (solo admin/superadmin). Los eventos quedan con org_id NULL; org_members se eliminan en cascada. */
  deleteOrganization: async (orgId: string): Promise<boolean> => {
    const result = await safeSupabaseOperation(
      () => supabase.from('organizations').delete().eq('id', orgId),
      'deleteOrganization'
    );
    return !result.error;
  },

  hasOrgAdminRole,
  getOrganizerOrgIds,
};
